<template>
  <div class="annotator-container">
    <div v-if="loading">
      <b-loading :is-full-page="false" :active="true"> </b-loading>
    </div>
    <div v-else-if="fetchFailed">
      <b-loading :is-full-page="false" :active="false"> </b-loading>
    </div>
    <div v-else class="annotator">
      <div
        v-if="tagObject.value"
        style="textAlign: center; font-size: 3em;backgroundColor: #ffdb4a"
      >
        {{ tagObject.name }}
      </div>
      <svg
        preserveAspectRatio="xMidYMid meet"
        :viewBox="viewBoxString"
        :style="svgStyle"
        @mousedown="onClickCanvas"
        @wheel="onWheel"
      >
        <image :href="state.selectedImageUrl" x="0" y="0" opacity="0.8" />
        <!-- 
        ----------------------------------------------------------------
        Interactive elements representing the current annotation state.
        ----------------------------------------------------------------
          They are drawn in the order that they are written, so make sure what you
          want to draw in the front is written later.
        -->
        <!-- edges -->
        <edges
          :setBlock="setBlock"
          :shouldBlock="shouldBlock"
          :EdgesSymbol="EdgesSymbol"
        />
        <!-- doors -->
        <doors :EdgesSymbol="EdgesSymbol" :shouldBlock="shouldBlock" />
        <!-- nodes -->
        <nodes
          :edgeRef="edgeRef"
          :setBlock="setBlock"
          :shouldBlock="shouldBlock"
          :NodesSymbol="NodesSymbol"
        />
        <!-- labels -->
        <labels :shouldBlock="shouldBlock" />
        <!-- misc -->
        <miscs :shouldBlock="shouldBlock" />
      </svg>
    </div>
  </div>
</template>

<script>
import {
  reactive,
  computed,
  watchEffect,
  onMounted,
  onUnmounted,
  provide,
  ref
} from "@vue/composition-api";
import { baseUrl } from "@/config";
import useNodes from "@/composables/useNodes";
import useEdges from "@/composables/useEdges";
import useLabels from "@/composables/useLabels";
import useMisc from "@/composables/useMisc";
import usePan from "@/composables/usePan";
import useTags from "@/composables/useTags";
import useSnackbar from "@/composables/useSnackbar";
import ui from "@/store/modules/ui";
import annotations from "@/store/modules/annotations";
import Nodes from "@/components/Nodes";
import Edges from "@/components/Edges";
import Doors from "@/components/Doors";
import Labels from "@/components/Labels";
import Miscs from "@/components/Miscs";

export default {
  components: {
    Nodes,
    Edges,
    Doors,
    Labels,
    Miscs
  },
  setup() {
    //
    // Local states
    // ---------------------------------------------------------------
    //
    const state = reactive({
      block: false,
      annoType: computed(() => ui.annoType),
      selectedImageId: computed(() => ui.selectedImageId),
      selectedImageUrl: computed(() => {
        const filename = annotations.getFilename;
        if (filename) {
          return baseUrl + "static/images/" + filename;
        } else {
          return "";
        }
      }),
      hasUnsavedChange: computed(() => {
        return ui.hasUnsavedChange;
      })
    });
    const setBlock = val => {
      state.block = val;
    };
    const viewOffset = computed(() => ui.viewOffset);
    //
    // Composables
    // ----------------------------------------
    // Passing refs through props will make them lose their responsibility,
    // so we chose to pipe them through useNode and useEdge to Nodes and Edges
    // where they will be updated with refs to elements.
    const nodeRef = ref([]);
    const edgeRef = ref([]);
    //
    // Provide nodes from this component.
    // This is needed because nodeRef has to be set at a child component,
    // and has to be used at multiple child componnets.
    //
    const NodesSymbol = Symbol();
    const nodesComposable = useNodes(nodeRef);
    provide(NodesSymbol, nodesComposable);
    const { place: placeNode } = nodesComposable;
    //
    // Provide edges from this component.
    // Same as above.
    //
    const EdgesSymbol = Symbol();
    const edgesComposable = useEdges(edgeRef, nodeRef);
    provide(EdgesSymbol, edgesComposable);
    const { place: placeLabel } = useLabels();
    const { place: placeMisc } = useMisc();
    const { pan } = usePan();
    const { tagObject } = useTags();
    const { open: openSnack } = useSnackbar();
    //
    // Event callbacks
    // ----------------------------------------
    //
    // If this returns false, don't execute action
    //
    const shouldBlock = computed(() => {
      if (state.block) return true;
      if (annotations.getState !== "wip") return true;
      return false;
    });
    //
    // notify change. hasUnsavedChange is used to alert the user of unsaved
    // change when excarding current modification to floorplan (fe, moving
    // to different route)
    //
    function notifyChange() {
      if (!state.hasUnsavedChange) {
        ui.setHasUnsavedChange(true);
      }
    }
    function onClickCanvas(e) {
      if (shouldBlock.value) return;

      switch (state.annoType) {
        case "pan":
          pan(e);
          break;
        case "place":
          notifyChange();
          placeNode(e);
          break;
        case "placeLabel":
          notifyChange();
          placeLabel(e);
          break;
        case "placeMisc":
          notifyChange();
          placeMisc(e);
          break;
      }
    }
    //
    // Svg zooming
    // ---------------------------------------------------------------
    //
    // Width of margin on both sides of the image, expressed by
    // the width's ratio to the image width.
    //
    const svgMarginRatio = computed(() => ui.svgMarginRatio);
    //
    // Zoom using the mouse wheel.
    //
    const onWheel = e => {
      e.preventDefault();
      // everything after the first division operator is for dampening the speed of
      // zooming for the sake of better ux
      let temp =
        svgMarginRatio.value +
        (e.deltaY * 0.001) / (2 * (1 - svgMarginRatio.value));
      // restrict value. margin shouldn't be below 0. play around with max.
      ui.setSvgMarginRatio(Math.min(0.8, Math.max(-0.4, temp)));
    };
    // Load image to check width and height
    const image = new Image();
    const imageSize = reactive({
      width: 0,
      height: 0
    });
    //
    // Reload image every time selectedImageUrl changes
    //
    watchEffect(() => {
      image.onload = () => {
        imageSize.width = image.width;
        imageSize.height = image.height;
      };
      image.src = state.selectedImageUrl;
    });
    //
    // Defines view box for svg canvas. adjusts every time the image loads
    // to fit image into canvas with specified margin ratio.
    //
    const viewBoxString = computed(() => {
      const minX =
        -(imageSize.width * svgMarginRatio.value) + viewOffset.value[0];
      const minY =
        -(imageSize.height * svgMarginRatio.value) + viewOffset.value[1];
      const width = imageSize.width * (1 + svgMarginRatio.value * 2);
      const height = imageSize.height * (1 + svgMarginRatio.value * 2);
      return `${minX} ${minY} ${width} ${height}`;
    });
    //
    // Styles and props
    // ---------------------------------------------------------------
    //
    const annotationState = computed(() => annotations.getState);
    const svgStyle = computed(() => {
      if (annotationState.value === "wip") {
        if (
          state.annoType === "place" ||
          state.annoType === "placeLabel" ||
          state.annoType === "placeMisc"
        ) {
          return {
            cursor: "pointer"
          };
        }
        return {
          cursor: "default"
        };
      } else {
        return {
          cursor: "not-allowed"
        };
      }
    });
    //
    // Keyboard Shortcuts
    // ----------------------------------------------------------------
    //
    const keyboardShortcuts = {
      p: () => {
        ui.setAnnoType("place");
      },
      a: () => {
        ui.setAnnoType("place");
      },
      d: () => {
        ui.setAnnoType("addDoor");
      },
      w: () => {
        ui.setAnnoType("addWindow");
      },
      m: () => {
        ui.setAnnoType("move");
      },
      g: () => {
        ui.setAnnoType("move");
      },
      r: () => {
        ui.setAnnoType("remove");
      },
      c: () => {
        ui.setAnnoType("connect");
      },
      s: () => {
        ui.setAnnoType("snap");
      },
      l: () => {
        ui.setAnnoType("placeLabel");
      },
      h: () => {
        ui.setAnnoType("pan");
      }
    };
    const onKeyDown = e => {
      Object.entries(keyboardShortcuts).forEach(([key, value]) => {
        if (e.key === key && !e.ctrlKey && !e.shiftKey) {
          e.preventDefault();
          value();
        }
      });
    };
    //
    // Created hook
    // ---------------------------------------------------------------
    //
    const loading = computed(() => ui.isLoadingAnnotation);
    const fetchFailed = computed(() => ui.fetchFailed);
    onMounted(async () => {
      ui.setIsLoadingAnnotation(true);
      // fetch user/project info from server
      try {
        await ui.fetchCurrentUser();
      } catch (error) {
        ui.setFetchFailed(true);
        console.error(
          "onMounted in Annotator failed at fetchCurrentUser: " + error
        );
        openSnack(error.message);
        ui.setIsLoadingAnnotation(false);
        return;
      }
      // if no floorplan is selected, try to select the first one in the project
      if (!ui.selectedImageId) {
        try {
          await ui.selectFirstImageInProject();
        } catch (error) {
          ui.setFetchFailed(true);
          console.error(
            "onMounted in Annotator failed at selectFirstImageInProject: " +
              error
          );
          openSnack(error.message);
          ui.setIsLoadingAnnotation(false);
          return;
        }
      }
      // fetch annotation from server
      try {
        await annotations.fetchAnnotation(state.selectedImageId);
      } catch (error) {
        console.error(
          "onMount in Annotator failed at fetchAnnotation: " + error
        );
        openSnack(error.message);
      } finally {
        ui.setIsLoadingAnnotation(false);
      }
      // Assign keyboard shortcuts
      document.addEventListener("keydown", onKeyDown);
    });

    onUnmounted(() => {
      // Remove keyboard shortcuts
      document.removeEventListener("keydown", onKeyDown);
    });

    return {
      NodesSymbol,
      EdgesSymbol,
      setBlock,
      shouldBlock,
      nodeRef,
      edgeRef,
      state,
      viewBoxString,
      tagObject,
      onClickCanvas,
      onWheel,
      svgStyle,
      loading,
      fetchFailed
    };
  }
};
</script>

<style lang="scss" scoped>
.annotator-container {
  position: relative;
  height: 100%;
  width: 100%;
}

.annotator {
  height: 100%;
  width: 100%;
}

svg {
  width: 100%;
  height: 100%;
}
</style>
